import { useMemo, useState } from "react";

import { debounce, throttle } from "lodash";
import useResizeObserver, { type ObservedSize } from "use-resize-observer";

export default useResizeObserver;

export const useDebouncedResizeObserver = ({
	wait,
	debounceSettings = { leading: true },
	initialSize = { width: undefined, height: undefined },
	...args
}: {
	wait: number;
	debounceSettings?: Parameters<typeof debounce>[2];
	initialSize?: { width?: number; height?: number };
} & Parameters<typeof useResizeObserver>[0]): ReturnType<typeof useResizeObserver> => {
	const [size, setSize] = useState<ObservedSize>({ width: undefined, height: undefined, ...initialSize });
	const onResize = useMemo(() => debounce(setSize, wait, debounceSettings), [wait, debounceSettings]);
	const { ref } = useResizeObserver({ onResize, ...args });

	return { ref, ...size };
};

export const useThrottledResizeObserver = ({
	wait,
	throttleSettings = {},
	initialSize = { width: undefined, height: undefined },
	...args
}: {
	wait: number;
	throttleSettings?: Parameters<typeof throttle>[2];
	initialSize?: { width?: number; height?: number };
} & Parameters<typeof useResizeObserver>[0]) => {
	const [size, setSize] = useState<ObservedSize>({ width: undefined, height: undefined, ...initialSize });
	const onResize = useMemo(() => throttle(setSize, wait, throttleSettings), [wait, throttleSettings]);
	const { ref } = useResizeObserver({ onResize, ...args });

	return { ref, ...size };
};
